/**
 * The customers API, typed at the edge of this package.
 *
 * Two shapes here carry decisions worth knowing before reading a screen.
 *
 * `Client` includes `internal_note` and `credit_used`. Neither belongs in
 * anything a customer reads; every endpoint returning this model is behind
 * `clients.client.read`, which no customer holds. This package is the back
 * office, so it gets both.
 *
 * `ClientUser` carries two scopes. `site_codes` is what somebody typed;
 * `effective_site_codes` is what that person can actually reach after
 * intersecting with their company's own scope. The screen shows the second,
 * because the first is a wish and the second is the truth.
 */

import type { ApiClient } from "@hardauto/core-api";

const BASE = "/api/v1";

export type PortalStatus = "pending" | "active" | "suspended";
export type ClientUserRole = "admin" | "buyer" | "viewer";
export type ClientUserState = "invited" | "active" | "suspended";

export interface Client {
  id: string;
  code: string;
  company_name: string;
  /** What the customer sees as their own name. Falls back to the legal one. */
  portal_name: string;
  tax_id: string;
  city: string;
  address: string;
  default_site_code: string;
  sales_rep_code: string;
  price_scale_code: string;
  credit_limit: string;
  /** Maintained by billing. Never editable from a screen. */
  credit_used: string;
  credit_available: string;
  credit_usage_pct: number;
  is_over_credit_limit: boolean;
  portal_status: PortalStatus;
  display_name: string;
  billing_contact: string;
  /** Internal. Never shown to the customer. */
  internal_note: string;
  created_at: string | null;
}

export interface ClientRow {
  client: Client;
  active_user_count: number;
  sales_rep_name: string;
}

export interface ClientList {
  items: ClientRow[];
  total: number;
  next_cursor: string | null;
}

export interface ClientUser {
  id: string;
  client_id: string;
  user_id: string;
  email: string;
  display_name: string;
  role: ClientUserRole;
  /** What was typed. Empty means "everything the company has". */
  site_codes: string[];
  /** What they can actually reach. This is the one to show. */
  effective_site_codes: string[];
  state: ClientUserState;
  last_seen_at: string | null;
}

export interface ScopeEntry {
  code: string;
  name: string;
  allowed: boolean;
}

export interface Scope {
  items: ScopeEntry[];
  /** The codes actually granted. Empty means no restriction — every depot. */
  granted: string[];
}

/** A commercial's login. `invited` until the invitation is used; `disabled` once Administration
 * suspended or closed it. */
export interface StaffAccount {
  user_id: string;
  email: string;
  display_name: string;
  status: "invited" | "active" | "disabled";
}

/** Whether the invitation e-mail went out, on the answer that gave a login. */
export type InvitationOutcome =
  "sent" | "failed" | "not_needed" | "no_address" | "unknown" | "disabled" | "skipped";

export interface SalesRep {
  code: string;
  name: string;
  territory: string;
  site_codes: string[];
  has_backoffice_access: boolean;
  user_id: string | null;
  /** The login, when the commercial has one. */
  account: StaffAccount | null;
  invitation?: InvitationOutcome | null;
  is_active: boolean;
  /** From the « Type client » list; empty when not set. */
  client_type: string;
  /** From the « Qualification commerciale » list; empty when not set. */
  qualification: string;
  /** The companies this commercial works for, by code. */
  company_codes: string[];
  client_count: number;
  credit_followed: string;
}

/** The two lists an administrator keeps for commercials. */
export type SalesRepOptionKind = "client_type" | "qualification";

export interface SalesRepOption {
  id: string;
  kind: SalesRepOptionKind;
  label: string;
}

export interface RepCompany {
  code: string;
  name: string;
}

/** What an import did. `reason` is a stable code the screen words. */
export interface SalesRepImportReport {
  created: number;
  updated: number;
  rejected: { row: number; reason: string; value: string }[];
}

/** What an assignment file did. */
export interface AssignmentImportReport {
  assigned: number;
  unchanged: number;
  rejected: { row: number; reason: string; value: string }[];
}

export interface PortfolioEntry {
  client: Client;
  active_user_count: number;
}

export interface Portfolio {
  items: PortfolioEntry[];
  /** Computed by the server, so two screens cannot disagree about the total. */
  total_credit_limit: string;
  total_credit_used: string;
  clients_over_limit: number;
}

export interface ClientQuery {
  search?: string;
  city?: string;
  site_code?: string;
  sales_rep_code?: string;
  /** Only the customers no commercial follows. */
  without_rep?: boolean;
  portal_status?: PortalStatus;
  /** The filter the recovery team opens this screen for. */
  over_limit_only?: boolean;
  after_id?: string;
  limit?: number;
}

function query(params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === "" || value === false) continue;
    search.set(key, String(value));
  }
  const rendered = search.toString();
  return rendered ? `?${rendered}` : "";
}

/** Every customers call, in one place. */
export class ClientsApi {
  readonly #client: ApiClient;

  constructor(client: ApiClient) {
    this.#client = client;
  }

  listClients(options: ClientQuery = {}): Promise<ClientList> {
    return this.#client.request<ClientList>(
      `${BASE}/clients${query({ ...options, limit: options.limit ?? 50 })}`,
    );
  }

  getClient(id: string): Promise<Client> {
    return this.#client.request<Client>(`${BASE}/clients/${encodeURIComponent(id)}`);
  }

  createClient(body: {
    code: string;
    company_name: string;
    tax_id?: string;
    city?: string;
    address?: string;
    default_site_code?: string;
    sales_rep_code?: string;
    price_scale_code?: string;
    credit_limit?: string;
  }): Promise<Client> {
    return this.#client.request<Client>(`${BASE}/clients`, { method: "POST", body });
  }

  updateClient(
    id: string,
    body: Partial<{
      company_name: string;
      tax_id: string;
      city: string;
      address: string;
      default_site_code: string;
      sales_rep_code: string;
      price_scale_code: string;
      credit_limit: string;
      display_name: string;
      billing_contact: string;
      internal_note: string;
    }>,
  ): Promise<Client> {
    return this.#client.request<Client>(`${BASE}/clients/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body,
    });
  }

  /**
   * Grant or withdraw portal access.
   *
   * Its own endpoint and its own permission, because withdrawing it cuts every
   * live session immediately — a different consequence from correcting a
   * billing contact, and in the design's matrix a different role.
   */
  setPortalStatus(id: string, portal_status: PortalStatus): Promise<Client> {
    return this.#client.request<Client>(`${BASE}/clients/${encodeURIComponent(id)}/portal-access`, {
      method: "POST",
      body: { portal_status },
    });
  }

  getScope(id: string): Promise<Scope> {
    return this.#client.request<Scope>(`${BASE}/clients/${encodeURIComponent(id)}/scope`);
  }

  /**
   * Replace the depot scope wholesale.
   *
   * The whole resulting set, not a delta: the screen is a set of toggles and
   * submitting the result is what stops two operators racing on the same tab.
   * An empty list means "no restriction", which is a real and common state.
   */
  setScope(id: string, site_codes: string[]): Promise<Scope> {
    return this.#client.request<Scope>(`${BASE}/clients/${encodeURIComponent(id)}/scope`, {
      method: "PUT",
      body: { site_codes },
    });
  }

  listClientUsers(id: string): Promise<{ items: ClientUser[] }> {
    return this.#client.request<{ items: ClientUser[] }>(
      `${BASE}/clients/${encodeURIComponent(id)}/users`,
    );
  }

  inviteClientUser(
    id: string,
    body: { email: string; role: ClientUserRole; site_codes?: string[]; display_name?: string },
  ): Promise<ClientUser> {
    return this.#client.request<ClientUser>(`${BASE}/clients/${encodeURIComponent(id)}/users`, {
      method: "POST",
      body,
    });
  }

  updateClientUser(
    linkId: string,
    body: Partial<{ role: ClientUserRole; site_codes: string[] }>,
  ): Promise<ClientUser> {
    return this.#client.request<ClientUser>(`${BASE}/client-users/${encodeURIComponent(linkId)}`, {
      method: "PATCH",
      body,
    });
  }

  setClientUserState(linkId: string, state: ClientUserState): Promise<ClientUser> {
    return this.#client.request<ClientUser>(
      `${BASE}/client-users/${encodeURIComponent(linkId)}/state`,
      { method: "POST", body: { state } },
    );
  }

  listSalesReps(includeInactive = true): Promise<{ items: SalesRep[] }> {
    return this.#client.request<{ items: SalesRep[] }>(
      `${BASE}/sales-reps${query({ include_inactive: includeInactive })}`,
    );
  }

  /** No code: the portal numbers its commercials (0001, 0002…). With an e-mail, their login is
   * opened (and invited) or linked in the same call. */
  createSalesRep(body: {
    name: string;
    email?: string;
    territory?: string;
    site_codes?: string[];
    client_type?: string;
    qualification?: string;
    company_codes?: string[];
  }): Promise<SalesRep> {
    return this.#client.request<SalesRep>(`${BASE}/sales-reps`, { method: "POST", body });
  }

  updateSalesRep(
    code: string,
    body: Partial<{
      name: string;
      territory: string;
      site_codes: string[];
      is_active: boolean;
      client_type: string;
      qualification: string;
      company_codes: string[];
    }>,
  ): Promise<SalesRep> {
    return this.#client.request<SalesRep>(`${BASE}/sales-reps/${encodeURIComponent(code)}`, {
      method: "PATCH",
      body,
    });
  }

  /** Opens the login at this address, or links the staff account that has it. */
  giveSalesRepAccount(code: string, email: string): Promise<SalesRep> {
    return this.#client.request<SalesRep>(
      `${BASE}/sales-reps/${encodeURIComponent(code)}/account`,
      { method: "POST", body: { email } },
    );
  }

  resendSalesRepInvitation(code: string): Promise<SalesRep> {
    return this.#client.request<SalesRep>(
      `${BASE}/sales-reps/${encodeURIComponent(code)}/account/invitation`,
      { method: "POST" },
    );
  }

  /** Unlinks the login and removes the Commercial profile; the account itself stays. */
  takeSalesRepAccount(code: string): Promise<SalesRep> {
    return this.#client.request<SalesRep>(
      `${BASE}/sales-reps/${encodeURIComponent(code)}/account`,
      { method: "DELETE" },
    );
  }

  /** Logins holding the Commercial profile that no commercial is linked to yet. */
  unlinkedSalesRepAccounts(): Promise<{ items: StaffAccount[] }> {
    return this.#client.request<{ items: StaffAccount[] }>(`${BASE}/sales-reps/unlinked-accounts`);
  }

  /** Give customers to a commercial; `null` gives them to nobody. */
  assignClients(
    client_ids: string[],
    sales_rep_code: string | null,
  ): Promise<{ assigned: number }> {
    return this.#client.request<{ assigned: number }>(`${BASE}/client-assignments`, {
      method: "POST",
      body: { client_ids, sales_rep_code },
    });
  }

  exportAssignments(): Promise<Blob> {
    return this.#client.requestBlob(`${BASE}/client-assignments/export`);
  }

  importAssignments(file: File): Promise<AssignmentImportReport> {
    const form = new FormData();
    form.append("upload", file, file.name);
    return this.#client.upload<AssignmentImportReport>(`${BASE}/client-assignments/import`, form);
  }

  salesRepOptions(): Promise<{ items: SalesRepOption[] }> {
    return this.#client.request<{ items: SalesRepOption[] }>(`${BASE}/sales-rep-options`);
  }

  addSalesRepOption(kind: SalesRepOptionKind, label: string): Promise<SalesRepOption> {
    return this.#client.request<SalesRepOption>(`${BASE}/sales-rep-options`, {
      method: "POST",
      body: { kind, label },
    });
  }

  removeSalesRepOption(id: string): Promise<unknown> {
    return this.#client.request(`${BASE}/sales-rep-options/${encodeURIComponent(id)}`, {
      method: "DELETE",
    });
  }

  salesRepCompanies(): Promise<{ items: RepCompany[] }> {
    return this.#client.request<{ items: RepCompany[] }>(`${BASE}/sales-rep-companies`);
  }

  /** The file the import reads, with the session's credentials — hence a blob, not a link. */
  exportSalesReps(): Promise<Blob> {
    return this.#client.requestBlob(`${BASE}/sales-reps/export`);
  }

  /** Through `upload`, so the browser writes the multipart boundary itself. */
  importSalesReps(file: File): Promise<SalesRepImportReport> {
    const form = new FormData();
    form.append("upload", file, file.name);
    return this.#client.upload<SalesRepImportReport>(`${BASE}/sales-reps/import`, form);
  }

  portfolio(code: string): Promise<Portfolio> {
    return this.#client.request<Portfolio>(
      `${BASE}/sales-reps/${encodeURIComponent(code)}/portfolio`,
    );
  }

  /**
   * The signed-in representative's own customers.
   *
   * A separate endpoint from `portfolio`, and the reason is the whole point:
   * this one resolves the representative from the authenticated account, so
   * asking for somebody else's portfolio by passing their code is not
   * expressible.
   */
  myPortfolio(): Promise<Portfolio> {
    return this.#client.request<Portfolio>(`${BASE}/me/portfolio`);
  }

  reassignPortfolio(from_code: string, to_code: string): Promise<{ clients_moved: number }> {
    return this.#client.request<{ clients_moved: number }>(`${BASE}/sales-reps/reassign`, {
      method: "POST",
      body: { from_code, to_code },
    });
  }
}
